import { useRequest } from 'ahooks'
import { Empty } from 'antd'
import { flatMapDeep } from 'lodash'
import moment from 'moment'
import React from 'react'
import { DragDropContext, DropResult, Droppable } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'

import { ReportSnapshot } from '@/schemas'

import { Node } from './report-snapshots.component'
import { ReportSnapshots } from './report-snapshots.component'

interface ReportGroupProps {
  domId: string
  selfId: string
  type: number
  name: string
  snapshots: any[]
  editable: boolean
  reportId: number
  refresh: () => void
  isShowKR: boolean
  isFull: boolean
  expandSnapshotIds: string[]
  objectiveCycleId?: number
}

const reorder = (res: Node<ReportSnapshot>[], sourceIndex: number, targetIndex: number) => {
  // const res = Array.from(list)
  const [item] = res.splice(sourceIndex, 1)
  res.splice(targetIndex, 0, item)
  return res.map((r, index) => ({
    ...r.data,
    order: index,
  }))
}

const flatten = (item: any): any[] => [item, flatMapDeep(item.children, flatten)]

export const ReportGroup: React.FC<ReportGroupProps> = ({
  domId,
  selfId,
  editable,
  reportId,
  refresh,
  snapshots,
  isShowKR,
  isFull,
  expandSnapshotIds,
}) => {
  const { t } = useTranslation()
  const { run: updateOrder } = useRequest(
    ({ children }) => ({
      url: '/api/report/updatePortfolioReportSnapshot',
      method: 'patch',
      data: {
        rootSelfId: selfId,
        reportId,
        children: children.map((c: ReportSnapshot) => ({
          id: c.id,
          parentId: c.parentId,
          path: c.path,
          reportId: c.reportId,
          selfId: c.selfId,
          type: c.type,
          name: c.name,
          departmentId: c.departmentId,
          email: c.email,
          description: c.description,
          endDate: c.endDate ? moment(c.endDate).format('YYYY-MM-DD') : undefined,
          startDate: c.startDate ? moment(c.startDate).format('YYYY-MM-DD') : undefined,
          risk: c.risk,
          progress: c.progress,
          weight: c.weight,
          status: c.status,
          moduleId: c.moduleId,
          order: c.order,
          operation: 1,
        })),
      },
    }),
    { manual: true, onSuccess: refresh }
  )

  const map = {}
  flatMapDeep(snapshots, flatten).forEach((s) => {
    if (map[s.data.parentId]) {
      map[s.data.parentId].children.push(s)
    } else {
      map[s.data.parentId] = Object.assign({}, { children: [s] })
    }
  })

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination || result.source.index === result.destination.index) return
    let sendOrder = []
    if (result.type !== selfId) {
      sendOrder = reorder(map[result.type].children || [], result.source.index, result.destination.index)
    } else {
      sendOrder = reorder(map['root'].children || [], result.source.index, result.destination.index)
    }
    await updateOrder({ children: sendOrder })
  }

  return (
    <>
      {snapshots.length > 0 ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={selfId!} type={selfId} isDropDisabled={!editable}>
            {(provided) => (
              <div id={domId} ref={provided.innerRef} {...provided.droppableProps}>
                <ReportSnapshots
                  selfId={selfId}
                  reportId={reportId}
                  snapshots={snapshots}
                  refresh={refresh}
                  editable={editable}
                  isShowKR={isShowKR}
                  isFull={isFull}
                  expandSnapshotIds={expandSnapshotIds}
                />
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Empty
          image='https://web-cdn.agora.io/workplace/icons/empty1.png'
          description={<span>{t('no data')}</span>}
        ></Empty>
      )}
    </>
  )
}
